/**
 * Schema version gate + migrations.
 *
 * - `checkSchemaVersion`: reads `meta.schema_version` and refuses to
 *   operate when on-disk is newer than supported (upgrade prompt).
 * - `bumpRevToOne` (schema 1 → 2): rewrites every rev=0 cell to rev=1
 *   across the four OCC-managed tabs (`transactions`, `books`,
 *   `categories`, `recurring`). Idempotent — guarded by reading
 *   `meta.schema_version` first; a second run is a no-op.
 * - `runMigrations`: orchestrator. Called from bootstrap / first read
 *   on every authenticated session.
 */

import type { SheetsClient } from './client';
import { metaRepo, parseMetaRows } from './repositories/meta';
import { SUPPORTED_SCHEMA_VERSION, TABS, tabRange } from './schema';
import type { TabName } from './schema';
import { nowIso, objectToRow, parseBool, rowToObject, tabRowRange } from './row-utils';
import { sanitizeTag } from '../tags';
import { SEED_CATEGORIES, SEED_SUBCATEGORIES } from './bootstrap';

export class SchemaTooNewError extends Error {
  readonly onDiskVersion: number;
  readonly supportedVersion: number;
  constructor(onDiskVersion: number, supportedVersion: number) {
    super(
      `Spreadsheet schema_version=${onDiskVersion} is newer than supported ${supportedVersion}; please refresh to upgrade.`,
    );
    this.name = 'SchemaTooNewError';
    this.onDiskVersion = onDiskVersion;
    this.supportedVersion = supportedVersion;
  }
}

export interface MigrationCheckResult {
  schemaVersion: number;
}

/**
 * Throws SchemaTooNewError if the spreadsheet has a higher schema_version
 * than this client supports. Missing meta key is treated as "version 1"
 * (legacy bootstrap predates schema_version stamping).
 *
 * `preloadedMetaRows`, when supplied, is a raw `meta` tab snapshot the
 * caller already fetched this run (data-layer spec requirement "Bootstrap
 * Read Deduplication") — reading `schema_version` from it skips a fresh
 * `meta` fetch. Omit it to fall back to the original always-fetch behavior.
 */
export async function checkSchemaVersion(
  client: SheetsClient,
  spreadsheetId: string,
  preloadedMetaRows?: string[][],
): Promise<MigrationCheckResult> {
  const raw = preloadedMetaRows
    ? parseMetaRows(preloadedMetaRows).find((r) => r.key === 'schema_version')?.value
    : await metaRepo.getValue({ client, spreadsheetId }, 'schema_version');
  const onDisk = raw == null || raw === '' ? 1 : Number.parseInt(raw, 10);
  const version = Number.isFinite(onDisk) ? onDisk : SUPPORTED_SCHEMA_VERSION;
  if (version > SUPPORTED_SCHEMA_VERSION) {
    throw new SchemaTooNewError(version, SUPPORTED_SCHEMA_VERSION);
  }
  return { schemaVersion: version };
}

const OCC_TABS: ReadonlyArray<TabName> = ['transactions', 'books', 'categories', 'recurring'];

/**
 * Migration v1 → v2. For every row in the four OCC-managed tabs whose
 * `rev` cell is empty or `0`, rewrite that row with `rev=1`.
 *
 * Idempotence: this function reads `meta.schema_version` first; if it's
 * already >= 2, it returns immediately without touching any tab. So
 * re-running on a partially-migrated workbook (e.g. interrupted run)
 * is safe — the second run picks up where the first left off, and a
 * third run sees schema_version=2 and bails.
 */
export async function bumpRevToOne(
  client: SheetsClient,
  spreadsheetId: string,
): Promise<void> {
  const ctx = { client, spreadsheetId };
  const current = await metaRepo.getValue(ctx, 'schema_version');
  const version = current == null || current === '' ? 1 : Number.parseInt(current, 10);
  if (Number.isFinite(version) && version >= 2) {
    return; // already migrated — no-op
  }

  for (const tab of OCC_TABS) {
    const headers = TABS[tab].headers;
    const revIdx = headers.indexOf('rev');
    if (revIdx === -1) continue;

    const resp = await client.valuesGet(spreadsheetId, tabRange(tab, { startRow: 2 }));
    const rows = (resp.values ?? []) as string[][];

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] ?? [];
      // Skip empty rows (id column blank).
      if ((r[0] ?? '') === '') continue;
      const cur = r[revIdx] ?? '';
      // Treat empty + '0' as legacy. Anything else (already-numeric >=1
      // or unparseable) is left alone — unparseable rows surface elsewhere
      // via parseInt10's stricter behaviour (Section 6).
      if (cur === '' || cur === '0') {
        const merged = rowToObject(headers, r);
        merged['rev'] = '1';
        const newValues = objectToRow(headers, merged);
        await client.valuesUpdate(
          spreadsheetId,
          tabRowRange(tab, i + 2),
          [newValues],
        );
      }
    }
  }

  await metaRepo.setValue(ctx, 'schema_version', '2');
}

/**
 * Migration v2 → v3 (book-architecture-v2). Three independent backfills:
 *
 *   1. `books.type`: any row whose `type` cell is empty → 'personal'.
 *   2. `categories.book_id`: any row whose `is_system` cell is NOT
 *      truthy (parsed via `parseBool`) and whose `book_id` is empty →
 *      assign the FIRST non-deleted book's id. System seeds keep
 *      `book_id=''` so they remain visible in every book.
 *   3. `transactions.tags`: any row with non-empty `project` AND empty
 *      `tags` → write `tags = '#' + project` and clear `project`.
 *
 * Idempotence: this function reads `meta.schema_version` first; if it's
 * already >= 3, it returns immediately. Even when invoked again on a
 * fully-migrated workbook (e.g. someone bumps the meta cell back to '2'
 * by hand), every row-level decision short-circuits when the destination
 * cell already has a value, so no double-writes occur.
 *
 * Sharp edges:
 *  - Step 2 needs at least one non-deleted book. We log + skip rather
 *    than throw, so a malformed workbook (no books) doesn't block the
 *    rest of the migration. The next bootstrap pass seeds the missing
 *    books via `verifyAndRepair`.
 *  - Step 3 sanitises the project string with `sanitizeTag` so any
 *    accidental commas inside a project name don't poison the tag
 *    delimiter.
 */
export async function migrateToV3(
  client: SheetsClient,
  spreadsheetId: string,
): Promise<void> {
  const ctx = { client, spreadsheetId };
  const current = await metaRepo.getValue(ctx, 'schema_version');
  const version = current == null || current === '' ? 1 : Number.parseInt(current, 10);
  if (Number.isFinite(version) && version >= 3) return;

  // ── 1. Books: backfill `type`. ────────────────────────────────────
  const bookHeaders = TABS.books.headers;
  const bookTypeIdx = bookHeaders.indexOf('type');
  if (bookTypeIdx !== -1) {
    const resp = await client.valuesGet(spreadsheetId, tabRange('books', { startRow: 2 }));
    const rows = (resp.values ?? []) as string[][];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] ?? [];
      if ((r[0] ?? '') === '') continue;
      const merged = rowToObject(bookHeaders, r);
      const cur = merged['type'] ?? '';
      if (cur === '') {
        merged['type'] = 'personal';
        const newValues = objectToRow(bookHeaders, merged);
        await client.valuesUpdate(
          spreadsheetId,
          tabRowRange('books', i + 2),
          [newValues],
        );
      }
    }
  }

  // ── 2. Categories: backfill `book_id` for legacy CUSTOM rows. ─────
  const catHeaders = TABS.categories.headers;
  const catBookIdIdx = catHeaders.indexOf('book_id');
  if (catBookIdIdx !== -1) {
    // Re-read books AFTER step 1 so we use the canonical state.
    const bookResp = await client.valuesGet(spreadsheetId, tabRange('books', { startRow: 2 }));
    const bookRows = (bookResp.values ?? []) as string[][];
    let firstBookId = '';
    for (const br of bookRows) {
      const obj = rowToObject(bookHeaders, br);
      if (!obj['id']) continue;
      if (obj['deleted_at']) continue;
      firstBookId = obj['id'] ?? '';
      break;
    }

    const catResp = await client.valuesGet(spreadsheetId, tabRange('categories', { startRow: 2 }));
    const catRows = (catResp.values ?? []) as string[][];
    for (let i = 0; i < catRows.length; i++) {
      const r = catRows[i] ?? [];
      if ((r[0] ?? '') === '') continue;
      const merged = rowToObject(catHeaders, r);
      const isSystem = parseBool(merged['is_system']);
      if (isSystem) continue; // system seeds stay book_id=''
      if ((merged['book_id'] ?? '') !== '') continue; // already migrated
      if (firstBookId === '') {
        // No book to assign — skip rather than throw. Next cold-load's
        // bootstrap will seed a book and a future re-run can backfill.
        // eslint-disable-next-line no-console
        console.warn('[Mochia/migrate] No non-deleted book found; skipping category', {
          id: merged['id'],
        });
        continue;
      }
      merged['book_id'] = firstBookId;
      const newValues = objectToRow(catHeaders, merged);
      await client.valuesUpdate(
        spreadsheetId,
        tabRowRange('categories', i + 2),
        [newValues],
      );
    }
  }

  // ── 2.5. Soft-delete the legacy `book-personal` book. ─────────────
  // Per dogfood feedback: the V1 default 「個人」 book is noise — the user
  // wants only 家庭 + 專案. Re-assign any tx that lived in `book-personal`
  // to the first remaining non-deleted book before tombstoning so we
  // don't orphan transactions.
  {
    const bookResp = await client.valuesGet(spreadsheetId, tabRange('books', { startRow: 2 }));
    const bookRows = (bookResp.values ?? []) as string[][];
    let personalRowIdx = -1;
    let firstSurvivorId = '';
    for (let i = 0; i < bookRows.length; i++) {
      const obj = rowToObject(bookHeaders, bookRows[i] ?? []);
      if (obj['id'] === 'book-personal' && !obj['deleted_at']) {
        personalRowIdx = i;
      } else if (!obj['deleted_at'] && obj['id']) {
        if (firstSurvivorId === '') firstSurvivorId = obj['id'];
      }
    }
    if (personalRowIdx !== -1 && firstSurvivorId !== '') {
      // Re-home transactions first.
      const txHeaders0 = TABS.transactions.headers;
      const txResp = await client.valuesGet(spreadsheetId, tabRange('transactions', { startRow: 2 }));
      const txRows = (txResp.values ?? []) as string[][];
      for (let i = 0; i < txRows.length; i++) {
        const r = txRows[i] ?? [];
        if ((r[0] ?? '') === '') continue;
        const obj = rowToObject(txHeaders0, r);
        if (obj['book_id'] !== 'book-personal') continue;
        obj['book_id'] = firstSurvivorId;
        await client.valuesUpdate(
          spreadsheetId,
          tabRowRange('transactions', i + 2),
          [objectToRow(txHeaders0, obj)],
        );
      }
      // Now tombstone the book row.
      const personalObj = rowToObject(bookHeaders, bookRows[personalRowIdx] ?? []);
      personalObj['deleted_at'] = new Date().toISOString();
      personalObj['is_default'] = 'false';
      await client.valuesUpdate(
        spreadsheetId,
        tabRowRange('books', personalRowIdx + 2),
        [objectToRow(bookHeaders, personalObj)],
      );
    }
  }

  // ── 3. Transactions: copy `project` → `tags` and clear `project`. ─
  const txHeaders = TABS.transactions.headers;
  const txTagsIdx = txHeaders.indexOf('tags');
  const txProjIdx = txHeaders.indexOf('project');
  if (txTagsIdx !== -1 && txProjIdx !== -1) {
    const resp = await client.valuesGet(spreadsheetId, tabRange('transactions', { startRow: 2 }));
    const rows = (resp.values ?? []) as string[][];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] ?? [];
      if ((r[0] ?? '') === '') continue;
      const merged = rowToObject(txHeaders, r);
      const project = (merged['project'] ?? '').trim();
      const tags = merged['tags'] ?? '';
      if (project === '' || tags !== '') continue;
      const tag = sanitizeTag(project);
      if (tag == null) continue;
      merged['tags'] = tag;
      merged['project'] = '';
      const newValues = objectToRow(txHeaders, merged);
      await client.valuesUpdate(
        spreadsheetId,
        tabRowRange('transactions', i + 2),
        [newValues],
      );
    }
  }

  await metaRepo.setValue(ctx, 'schema_version', '3');
}

/**
 * Migration v3 → v4. Cleanup for sheets that were seeded during the brief
 * window where `books.type` lived in the middle of the headers. Detects
 * rows whose `is_default` cell holds a type-code ('personal'|'family'|
 * 'project') instead of a boolean — those rows are misaligned and need
 * to be rewritten. Also force-tombstones any `book-personal` row that
 * the v3 cleanup missed (e.g. when the user already had v3 schema
 * stamped before that step was added).
 *
 * Safe to call on a clean workbook — does nothing.
 */
const TYPE_VALUES = new Set(['personal', 'family', 'project']);

export async function migrateToV4(
  client: SheetsClient,
  spreadsheetId: string,
): Promise<void> {
  const ctx = { client, spreadsheetId };
  const current = await metaRepo.getValue(ctx, 'schema_version');
  const version = current == null || current === '' ? 1 : Number.parseInt(current, 10);
  if (Number.isFinite(version) && version >= 4) return;

  const bookHeaders = TABS.books.headers;
  const bookResp = await client.valuesGet(spreadsheetId, tabRange('books', { startRow: 2 }));
  const bookRows = (bookResp.values ?? []) as string[][];

  // Pass 1 — detect + repair misaligned rows.
  for (let i = 0; i < bookRows.length; i++) {
    const r = bookRows[i] ?? [];
    if ((r[0] ?? '') === '') continue;
    // Read using current header positions:
    const isDefaultCell = r[bookHeaders.indexOf('is_default')] ?? '';
    const isMisaligned = TYPE_VALUES.has(isDefaultCell);
    if (!isMisaligned) continue;

    // Old layout was [id,name,color_token,TYPE,is_default,rev,created_at,
    // updated_at,deleted_at] — 9 cols, type at index 3. Reconstruct
    // explicitly from positional reads.
    const repaired = {
      id: r[0] ?? '',
      name: r[1] ?? '',
      color_token: r[2] ?? '',
      type: r[3] ?? 'personal',
      is_default: r[4] ?? 'false',
      rev: r[5] ?? '1',
      created_at: r[6] ?? '',
      updated_at: r[7] ?? '',
      deleted_at: r[8] ?? '',
    };
    const newValues = objectToRow(bookHeaders, repaired);
    await client.valuesUpdate(
      spreadsheetId,
      tabRowRange('books', i + 2),
      [newValues],
    );
  }

  // Pass 2 — force-tombstone any book-personal that survived v3.
  // Re-fetch in case pass 1 changed offsets.
  const refetch = await client.valuesGet(spreadsheetId, tabRange('books', { startRow: 2 }));
  const refetchRows = (refetch.values ?? []) as string[][];
  let personalIdx = -1;
  // Re-home preference order: book-family > book-project > first non-deleted.
  // book-family is the new default seed; surfacing legacy txs there is the
  // least surprising landing spot for the user.
  let familyId = '';
  let projectId = '';
  let fallbackId = '';
  for (let i = 0; i < refetchRows.length; i++) {
    const obj = rowToObject(bookHeaders, refetchRows[i] ?? []);
    const id = obj['id'];
    if (!id) continue;
    if (id === 'book-personal' && !obj['deleted_at']) {
      personalIdx = i;
      continue;
    }
    if (obj['deleted_at']) continue;
    if (id === 'book-family') familyId = id;
    else if (id === 'book-project') projectId = id;
    else if (fallbackId === '') fallbackId = id;
  }
  const reHomeTarget = familyId || projectId || fallbackId;
  if (personalIdx !== -1 && reHomeTarget !== '') {
    // Re-home transactions still pointing at book-personal.
    const txHeaders = TABS.transactions.headers;
    const txResp = await client.valuesGet(
      spreadsheetId,
      tabRange('transactions', { startRow: 2 }),
    );
    const txRows = (txResp.values ?? []) as string[][];
    for (let i = 0; i < txRows.length; i++) {
      const r = txRows[i] ?? [];
      if ((r[0] ?? '') === '') continue;
      const obj = rowToObject(txHeaders, r);
      if (obj['book_id'] !== 'book-personal') continue;
      obj['book_id'] = reHomeTarget;
      await client.valuesUpdate(
        spreadsheetId,
        tabRowRange('transactions', i + 2),
        [objectToRow(txHeaders, obj)],
      );
    }
    const personalObj = rowToObject(bookHeaders, refetchRows[personalIdx] ?? []);
    personalObj['deleted_at'] = new Date().toISOString();
    personalObj['is_default'] = 'false';
    await client.valuesUpdate(
      spreadsheetId,
      tabRowRange('books', personalIdx + 2),
      [objectToRow(bookHeaders, personalObj)],
    );
  }

  await metaRepo.setValue(ctx, 'schema_version', '4');
}

/**
 * Migration v4 → v5. EXCEPTIONAL — this one reorders the `transactions`
 * tab columns (the rest of the schema stays append-only):
 *   - `tags` moves from the trailing append slot to right after `note`.
 *   - `time` (added in v4) is dropped; its value is folded into `date`
 *     as an optional `THH:MM` suffix.
 *
 * Both changes were dogfood requests: the appended-by-version columns
 * landed at the far right of the workbook and made it visually noisy
 * to scan by hand.
 *
 * Algorithm — atomic over each row, idempotent across runs:
 *   1. Read header row. If it already matches v5 layout, no-op.
 *   2. Read all data rows under the OLD positional layout.
 *   3. For each row: build a map by old-header position; fold time into
 *      date; write back using NEW positional layout.
 *   4. Replace the header row with the v5 layout.
 *   5. Bump `meta.schema_version` to `'5'`.
 *
 * Sharp edges:
 *   - We do NOT shrink the sheet's column count. After the rewrite,
 *     column 17 (was `time`) holds `''` for every data row but the
 *     header at A1 only spans columns 1–16. Sheets tolerates this; the
 *     Repo's parser is header-driven so the trailing column is ignored.
 *   - If the migration is interrupted mid-rewrite (e.g. tab close), a
 *     re-run notices schema_version is still 4 (or absent / 5) and
 *     either retries or no-ops. Per-row writes use the v5 column order
 *     unconditionally so a partial run doesn't corrupt rows already
 *     migrated — they get rewritten with the same content.
 */
export async function migrateToV5(
  client: SheetsClient,
  spreadsheetId: string,
): Promise<void> {
  const ctx = { client, spreadsheetId };
  const current = await metaRepo.getValue(ctx, 'schema_version');
  const version = current == null || current === '' ? 1 : Number.parseInt(current, 10);
  if (Number.isFinite(version) && version >= 5) return;

  const newHeaders = TABS.transactions.headers;
  // Old (v3 / v4) headers — the layout we may be coming FROM. Cell
  // positions in the sheet currently follow this order.
  const oldHeaders: ReadonlyArray<string> = [
    'id', 'book_id', 'date', 'amount_minor', 'currency', 'kind',
    'category_id', 'subcategory_id', 'note', 'project',
    'recurring_id', 'rev', 'created_at', 'updated_at', 'deleted_at',
    'tags', 'time',
  ];

  // Read the header row to detect whether the sheet already has v5.
  const lastColOld = String.fromCharCode(64 + oldHeaders.length); // A..Q (17)
  const headerResp = await client.valuesGet(
    spreadsheetId,
    `transactions!A1:${lastColOld}1`,
  );
  const headerRow = (headerResp.values?.[0] ?? []) as string[];
  const looksLikeV5 =
    headerRow.length >= newHeaders.length &&
    headerRow.slice(0, newHeaders.length).every(
      (cell, i) => cell === newHeaders[i],
    );
  if (looksLikeV5) {
    // Layout is already v5 — just stamp the version meta and bail.
    await metaRepo.setValue(ctx, 'schema_version', '5');
    return;
  }

  // Read all data rows under the OLD positional layout.
  const dataResp = await client.valuesGet(
    spreadsheetId,
    `transactions!A2:${lastColOld}`,
  );
  const dataRows = (dataResp.values ?? []) as string[][];

  // Rewrite each non-empty row in place using the v5 column order.
  for (let i = 0; i < dataRows.length; i++) {
    const r = dataRows[i] ?? [];
    if ((r[0] ?? '') === '') continue;
    const obj = rowToObject(oldHeaders, r);
    const time = (obj['time'] ?? '').trim();
    if (time !== '') {
      const date = obj['date'] ?? '';
      // Avoid double-folding if a previous interrupted run already merged.
      if (date && !date.includes('T')) {
        obj['date'] = `${date}T${time.length >= 5 ? time.slice(0, 5) : time}`;
      }
    }
    const newValues = objectToRow(newHeaders, obj);
    await client.valuesUpdate(
      spreadsheetId,
      tabRowRange('transactions', i + 2),
      [newValues],
    );
  }

  // Replace the header row. We write the v5 headers (16 cols); the
  // 17th column header position (was `time`) is left as-is from the
  // sheet — empty data cells make it irrelevant. A1: notation writes
  // exactly newHeaders.length cells, leaving column 17 untouched.
  await client.valuesUpdate(
    spreadsheetId,
    `transactions!A1:${String.fromCharCode(64 + newHeaders.length)}1`,
    [[...newHeaders]],
  );
  // Also blank the leftover header label at column 17 so it doesn't
  // confuse a future bare-eye scan of the sheet.
  if (oldHeaders.length > newHeaders.length) {
    const leftoverCol = String.fromCharCode(64 + oldHeaders.length);
    await client.valuesUpdate(spreadsheetId, `transactions!${leftoverCol}1`, [
      [''],
    ]);
  }

  await metaRepo.setValue(ctx, 'schema_version', '5');
}

/**
 * The 3 system sub-categories added in v6 — backfilled onto already-
 * bootstrapped workbooks. Ids are the single source of truth; the row
 * contents (parent_id / name / icon / sort_order / kind) are pulled from
 * `SEED_SUBCATEGORIES` so we never drift from the fresh-bootstrap seed.
 */
const V6_NEW_SUB_IDS: ReadonlyArray<string> = [
  'sub-pet-supplement',
  'sub-daily-clean',
  'sub-sub-content',
];

/**
 * Conditional v6 renames. Each row's `name` is overwritten to `to` ONLY when
 * the current cell still equals `from` (the old default). A user who renamed
 * the row to anything else keeps their value — design Decision「更名採『僅在
 * 名稱仍等於舊預設值時才覆寫』」.
 */
const V6_RENAMES: ReadonlyArray<{ id: string; from: string; to: string }> = [
  { id: 'sub-sub-cloud', from: '雲端空間', to: '雲端服務' },
  { id: 'sub-edu-online', from: '線上訂閱', to: '課程訂閱' },
];

/**
 * Migration v5 → v6 (expand-category-taxonomy). Backfills an already-
 * bootstrapped `categories` tab to the current SEED taxonomy:
 *
 *   1. Append the 3 new system subs (`sub-pet-supplement`, `sub-daily-clean`,
 *      `sub-sub-content`) — one row each, only for ids not already present.
 *      Field layout mirrors `buildSubCategoryRows`: `book_id=''`,
 *      `is_system='true'`, `color_token` inherited from the parent main
 *      (resolved from `SEED_CATEGORIES`), `kind='expense'`, `rev='1'`,
 *      `created_at`/`updated_at` = migration run time.
 *   2. Conditionally rename `sub-sub-cloud` (雲端空間→雲端服務) and
 *      `sub-edu-online` (線上訂閱→課程訂閱) — only when the current `name`
 *      still equals the old default; a user-renamed row is left untouched.
 *
 * This migration is NOT gated by the "Categories Seed Top-up Skipped When
 * Sheet Has Been Bootstrapped" guard — that guard is cold-load-only; this
 * runs on the schema-version-gated path.
 *
 * Idempotence: reads `meta.schema_version` first; if already >= 6 it returns
 * immediately WITHOUT any read/write against the categories tab. A re-run
 * after an interrupted pass appends only still-missing ids (existence-checked
 * per id) and re-applies each rename only while the cell is still the old
 * default, so a third run is a no-op once `schema_version` reaches 6.
 */
export async function migrateToV6(
  client: SheetsClient,
  spreadsheetId: string,
): Promise<void> {
  const ctx = { client, spreadsheetId };
  const current = await metaRepo.getValue(ctx, 'schema_version');
  const version = current == null || current === '' ? 1 : Number.parseInt(current, 10);
  if (Number.isFinite(version) && version >= 6) return;

  const catHeaders = TABS.categories.headers;
  const idIdx = catHeaders.indexOf('id');
  const nameIdx = catHeaders.indexOf('name');

  const resp = await client.valuesGet(spreadsheetId, tabRange('categories', { startRow: 2 }));
  const rows = (resp.values ?? []) as string[][];

  // Index existing rows by id → 1-indexed sheet row number.
  const presentIds = new Set<string>();
  const rowNumberById = new Map<string, number>();
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i] ?? [];
    const id = r[idIdx] ?? '';
    if (id === '') continue;
    presentIds.add(id);
    rowNumberById.set(id, i + 2);
  }

  // ── 1. Append missing new subs. ───────────────────────────────────
  const parentColor = new Map(SEED_CATEGORIES.map((c) => [c.id, c.color_token]));
  const now = nowIso();
  const toAppend: string[][] = [];
  for (const subId of V6_NEW_SUB_IDS) {
    if (presentIds.has(subId)) continue;
    const seed = SEED_SUBCATEGORIES.find((s) => s.id === subId);
    if (!seed) continue; // defensive — SEED constant is the source of truth
    toAppend.push(
      objectToRow(catHeaders, {
        id: seed.id,
        book_id: '',
        parent_id: seed.parent_id,
        name: seed.name,
        kind: seed.kind,
        // Subs inherit color from their parent main, same as fresh bootstrap.
        color_token: parentColor.get(seed.parent_id) ?? '--cat-other',
        icon: seed.icon,
        sort_order: String(seed.sort_order),
        is_system: 'true',
        rev: '1',
        created_at: now,
        updated_at: now,
        deleted_at: '',
      }),
    );
  }
  if (toAppend.length > 0) {
    await client.valuesAppend(spreadsheetId, `${TABS.categories.name}!A1`, toAppend);
  }

  // ── 2. Conditional renames (skip user-renamed rows). ──────────────
  for (const { id, from, to } of V6_RENAMES) {
    const rowNumber = rowNumberById.get(id);
    if (rowNumber == null) continue; // row absent — nothing to rename
    const r = rows[rowNumber - 2] ?? [];
    if ((r[nameIdx] ?? '') !== from) continue; // user-renamed / already new
    const merged = rowToObject(catHeaders, r);
    merged['name'] = to;
    await client.valuesUpdate(
      spreadsheetId,
      tabRowRange('categories', rowNumber),
      [objectToRow(catHeaders, merged)],
    );
  }

  await metaRepo.setValue(ctx, 'schema_version', '6');
}

/**
 * Run all migrations needed to bring the workbook up to
 * `SUPPORTED_SCHEMA_VERSION`. Called from bootstrap. Safe to call on
 * already-migrated workbooks (each migration is idempotent).
 */
/**
 * `knownSchemaVersion`, when supplied, is a version already computed this
 * run (typically by `checkSchemaVersion` moments earlier — data-layer spec
 * requirement "Bootstrap Read Deduplication") — using it skips a second
 * `meta.schema_version` fetch. Omit it to fall back to the original
 * always-fetch behavior.
 */
export async function runMigrations(
  client: SheetsClient,
  spreadsheetId: string,
  knownSchemaVersion?: number,
): Promise<void> {
  let current: number;
  if (knownSchemaVersion != null) {
    current = knownSchemaVersion;
  } else {
    const ctx = { client, spreadsheetId };
    const raw = await metaRepo.getValue(ctx, 'schema_version');
    const onDisk = raw == null || raw === '' ? 1 : Number.parseInt(raw, 10);
    current = Number.isFinite(onDisk) ? onDisk : 1;
  }

  if (current < 2) {
    await bumpRevToOne(client, spreadsheetId);
  }
  if (current < 3) {
    await migrateToV3(client, spreadsheetId);
  }
  if (current < 4) {
    await migrateToV4(client, spreadsheetId);
  }
  if (current < 5) {
    await migrateToV5(client, spreadsheetId);
  }
  if (current < 6) {
    await migrateToV6(client, spreadsheetId);
  }
}
